import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";

import { highlightParts, shownCode, type SmartOption } from "./smartMatch";
import "./smartSelect.css";

/** `text` with the parts matching `query` marked (UX12). */
export function Highlighted({ text, query }: { text: string; query: string }) {
  const parts = highlightParts(text, query);
  return (
    <>
      {parts.map((part, index) =>
        part.match ? (
          <mark key={index} className="positive-smart-mark">
            {part.text}
          </mark>
        ) : (
          part.text
        ),
      )}
    </>
  );
}

/** The text of an option row: code, label (highlighted) and detail. */
export function OptionText({ option, query }: { option: SmartOption; query: string }) {
  const code = shownCode(option);
  return (
    <>
      {code !== null && (
        <span className="positive-smart-code" dir="ltr">
          <Highlighted text={code} query={query} />
        </span>
      )}
      <span className="positive-smart-label">
        <Highlighted text={option.label} query={query} />
      </span>
      {option.detail && (
        <span className="positive-smart-detail">
          <Highlighted text={option.detail} query={query} />
        </span>
      )}
    </>
  );
}

interface Placement {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
}

/** Room the list wants under (or above) its field. */
const listRem = 18;

function place(anchor: HTMLElement): Placement {
  const rect = anchor.getBoundingClientRect();
  const rootSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const wanted = listRem * rootSize;
  const below = window.innerHeight - rect.bottom - 8;
  const above = rect.top - 8;
  const width = Math.min(Math.max(rect.width, 18 * rootSize), window.innerWidth - 16);
  const rtl = getComputedStyle(anchor).direction === "rtl";
  // Aligned with the field's start edge, kept inside the window.
  const start = rtl ? rect.right - width : rect.left;
  const left = Math.min(Math.max(start, 8), Math.max(window.innerWidth - width - 8, 8));
  const upwards = below < Math.min(wanted, 160) && above > below;
  return upwards
    ? { bottom: window.innerHeight - rect.top + 2, left, width, maxHeight: Math.min(wanted, Math.max(above, 96)) }
    : { top: rect.bottom + 2, left, width, maxHeight: Math.min(wanted, Math.max(below, 96)) };
}

export interface SmartListProps {
  id: string;
  anchor: RefObject<HTMLElement | null>;
  label: string;
  multiple?: boolean;
  /** Index of the highlighted row, scrolled into view. */
  active: number;
  header?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  "data-testid"?: string | undefined;
}

/**
 * The drop-down list of a smart field. It is a manual popover (the browser's top layer): it shows above dialogs and is
 * never clipped by a scrolling container, while staying inside the field in the DOM (focus, dialogs and the grid treat
 * clicks on it as clicks in the field). It sits under the field, or above it when the window has more room there.
 */
export function SmartList({
  id,
  anchor,
  label,
  multiple = false,
  active,
  header,
  children,
  footer,
  "data-testid": testId,
}: SmartListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);

  useLayoutEffect(() => {
    const list = listRef.current;
    // Chromium, Firefox and Safari have the Popover API; jsdom has not (the list is simply rendered there).
    if (list && typeof list.showPopover === "function" && !list.matches(":popover-open")) list.showPopover();
    const update = () => {
      if (anchor.current) setPlacement(place(anchor.current));
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchor]);

  useEffect(() => {
    const row = listRef.current?.querySelector<HTMLElement>("[data-active]");
    if (row && typeof row.scrollIntoView === "function") row.scrollIntoView({ block: "nearest" });
  }, [active]);

  return (
    <div
      ref={listRef}
      popover="manual"
      className="positive-smart-popup"
      data-testid={testId}
      style={
        placement
          ? {
              top: placement.top ?? "auto",
              bottom: placement.bottom ?? "auto",
              left: placement.left,
              width: placement.width,
              maxHeight: placement.maxHeight,
            }
          : undefined
      }
      onMouseDown={(event) => {
        // Keep the focus in the field: a click on the list must not end the edit before it picks.
        event.preventDefault();
      }}
    >
      {header}
      <div id={id} role="listbox" aria-label={label} aria-multiselectable={multiple} className="positive-smart-list">
        {children}
      </div>
      {footer}
    </div>
  );
}
